import { useEffect, useState } from 'react';
import { lastDayOfMonth, subDays } from 'date-fns';
import { useLazyQuery, useMutation } from '@apollo/client';
import { BooleanParam, useQueryParam } from 'next-query-params';

import { strScalarDate } from '../components/ui-kit/FormatDate';

import {
  GET_LOCATION,
  PACKAGE_QUOTES_MUTATION,
  CUSTOMIZE_PACKAGE_QUOTE,
  GENERATE_ADD_ONS,
  CREATE_RESERVATION,
  GET_QUOTE_SUMMARY,
  GET_QUOTE_SUMMARY_V2,
  CREATE_FLEXIBLE_PACKAGE_QUOTES,
  GET_LOCATION_ID,
  CREATE_FLEXIBLE_SERVICE_QUOTES_V2,
  GET_PACKAGE_QUOTE_BY_ID_IS_AVAILABLE,
  GET_ELIGIBLE_PACKAGE,
  SAVE_QUOTE,
  GET_PACKAGE_QUOTE_BY_ID,
} from '../gql/api-v2';

import { omitTypename } from './shared';
import convertToMilitary from '../utils/datetime/convertToMilitary';
import mapPropertyInfo from '../utils/locations/mapPropertyInfo';
import formatAddress from '../utils/formatAddress';
import getErrorType from '../utils/error/errorType';

import { AVAILABILITY_AB_TEST_MARKETS } from '../constants';

const getContext = (ab, b) =>
  ab && b
    ? {
        context: {
          headers: {
            FEATURE_AVAILABILITY: ab,
          },
        },
      }
    : {};

/* 
  Takes an array of addresss
  Returns an array of locations
*/
export const useLocationsQuery = () => {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [ab, setAB] = useQueryParam('ab', BooleanParam);

  const [getLocation, { loading, error }] = useLazyQuery(GET_LOCATION, {
    onCompleted: response => {
      setData([...data, response.location]);
    },
  });

  const getLocations = locations => {
    setSelected(locations);
    const variables = locations.map(({ address }) => ({ address: omitTypename(address) }));
    getLocation({ variables: variables[0] });
  };

  useEffect(() => {
    if (data.length < selected.length) {
      const nextSelected = selected[data.length];
      getLocation({
        variables: { address: omitTypename(nextSelected?.address) },
      });

      // checks to see if there's dupe address in the selected locations and appends it to the data array
      // since GQL does not make the API call since we are sending the exact same variables.
      const dupeAddress = data.find(location =>
        nextSelected.formattedAddress.includes(formatAddress({ ...location.address })),
      );
      if (dupeAddress) {
        setData([...data, dupeAddress]);
      }
    }
    if (data.length > 0 && !ab) {
      if (AVAILABILITY_AB_TEST_MARKETS.some(market => market === data[0].marketCode)) {
        setAB(1);
      }
    }
  }, [data]);

  return [
    data.length !== selected.length ? [] : data,
    getLocations,
    { loading: loading || data.length !== selected.length, error },
  ];
};

/*
  Gets location id from address input
*/
export const useLocationIdQuery = () => {
  const [getLocationId, { data, loading, error }] = useLazyQuery(GET_LOCATION_ID);

  const getId = address => {
    getLocationId({
      variables: {
        address: omitTypename(address),
      },
    });
  };

  return [
    data?.location?.id,
    getId,
    {
      error,
      loading,
    },
  ];
};

export function useEligiblePackageQuery() {
  const [getEligiblePackages, { data, loading, error }] = useLazyQuery(GET_ELIGIBLE_PACKAGE);

  const getPackage = (locationIds, group) => {
    getEligiblePackages({
      variables: {
        locationIds,
        group,
      },
    });
  };

  return [data?.eligiblePackage, getPackage, { error, loading }];
}

/* 
  Takes move details
  Returns an array of package quotes
*/
export function usePackageQuotesMutation() {
  const [mutate, { data, loading, error }] = useMutation(PACKAGE_QUOTES_MUTATION);

  const [ab] = useQueryParam('ab', BooleanParam);
  const [b] = useQueryParam('b', BooleanParam);

  const context = getContext(ab, b);

  const createPackageQuotes = async ({ locations, date, time, group, metadata, workers, duration }) => {
    mutate({
      variables: {
        date: strScalarDate(date),
        time: convertToMilitary(time),
        group,
        locations: mapPropertyInfo(locations, metadata),
        ...(group !== 'MOVERS_WITH_TRUCK' && {
          workers,
          duration,
        }),
        isFeeQuotes: process.env.NEXT_PUBLIC_FF_BASE_FEE === 'true',
      },
      ...context,
    });
  };

  return [
    data?.packageQuotesV2,
    createPackageQuotes,
    {
      loading,
      error,
    },
  ];
}

/* 
  Takes a customizable package id and new service duration/workers
  Returns an updated package quote with new duration/workers
*/
export function useCustomizePackageQuoteMutation() {
  const [mutate, { error, data, loading }] = useMutation(CUSTOMIZE_PACKAGE_QUOTE);

  const [ab] = useQueryParam('ab', BooleanParam);
  const [b] = useQueryParam('b', BooleanParam);

  const context = getContext(ab, b);

  const customizePackage = async ({ packageQuoteId, customServices }) => {
    mutate({
      variables: {
        packageQuoteId,
        customServices,
      },
      ...context,
    });
  };

  return [
    data?.customizePackageQuote,
    customizePackage,
    {
      error,
      loading,
    },
  ];
}

/*
  Generate add ons based on the selected package quote.
*/
export function useGenerateAddOns() {
  const [mutate, { data, loading, error }] = useMutation(GENERATE_ADD_ONS);
  const [ab] = useQueryParam('ab', BooleanParam);
  const [b] = useQueryParam('b', BooleanParam);

  const context = getContext(ab, b);

  const generateAddOns = async packageQuoteId => {
    mutate({
      variables: {
        packageQuoteId,
      },
      ...context,
    });
  };

  return [
    data?.serviceQuotes,
    generateAddOns,
    {
      error,
      loading,
    },
  ];
}

/* 
  Takes a package ID and customer info fields to create a draft ldm lead.
*/
export function useCreateDraftReservation() {
  const [mutate, { error, data, loading }] = useMutation(CREATE_RESERVATION);

  const createDraftReservation = async ({ packageQuoteID, customer, opportunitySFID }) =>
    mutate({
      variables: {
        reservation: {
          packageQuoteID,
          additionalServices: [],
          promoCode: [],
          customer,
          paymentMethod: {
            provider: 'DRAFT',
            token: 'tok_visa',
          },
          opportunitySFID,
        },
      },
    });

  return [
    data?.reserve,
    createDraftReservation,
    {
      error: error?.message,
      loading,
    },
  ];
}

/* 
  Takes a package, services, customer details, payment info, other move details
  Creates a reservation, returns an ID then authenticates the user, and caches the created reservation.
*/
export function useCreateReservationMutation() {
  const [mutate, { error, data, loading }] = useMutation(CREATE_RESERVATION);

  const createReservation = async ({
    packageQuoteID,
    additionalServices,
    promoCode,
    customer,
    paymentMethod,
    notes,
    bookedByUserEmail,
    opportunitySFID,
  }) =>
    mutate({
      variables: {
        reservation: {
          packageQuoteID,
          additionalServices,
          promoCode,
          customer,
          paymentMethod,
          notes,
          bookedByUserEmail,
          opportunitySFID,
        },
      },
    });
  return [
    data?.reserve,
    createReservation,
    {
      loading,
      error: error?.message,
      errorType: getErrorType(error),
    },
  ];
}

export function useQuoteSummaryQuery() {
  const [getQuoteSummary, { data, loading, error }] = useLazyQuery(GET_QUOTE_SUMMARY);

  const getSummary = ({ packageQuoteID, promoCode, additionalServices }) => {
    getQuoteSummary({
      variables: {
        input: {
          packageQuoteID,
          promoCode,
          additionalServices,
        },
      },
    });
  };

  return [
    data?.quoteSummary,
    getSummary,
    {
      error,
      loading,
    },
  ];
}

export function useQuoteSummaryV2Query() {
  const [getQuoteSummary, { data, loading, error }] = useLazyQuery(GET_QUOTE_SUMMARY_V2);

  const getSummary = ({ packageQuoteID, promoCode, additionalServices }) => {
    getQuoteSummary({
      variables: {
        input: {
          packageQuoteID,
          promoCode,
          additionalServices,
        },
      },
    });
  };

  return [
    data?.quoteSummaryV2,
    getSummary,
    {
      error,
      loading,
    },
  ];
}

export function useCreateFlexiblePackageQuotes({
  slots,
  group,
  packageID,
  locations,
  customServices,
  workers,
  duration,
  metadata,
}) {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ab] = useQueryParam('ab', BooleanParam);
  const [b] = useQueryParam('b', BooleanParam);

  const [mutate] = useMutation(CREATE_FLEXIBLE_PACKAGE_QUOTES);

  const context = getContext(ab, b);

  const update = async date => {
    setLoading(true);

    const promises = slots.map(slot =>
      Promise.resolve(
        mutate({
          variables: {
            start: date,
            end: strScalarDate(lastDayOfMonth(new Date(date).setMonth(new Date(date).getMonth() + 1))),
            time: convertToMilitary(slot),
            group,
            packageID,
            locations: mapPropertyInfo(locations, metadata),
            customServices,
            workers,
            duration,
          },
          ...context,
        }),
      ),
    );

    try {
      const response = await Promise.all(promises);
      const quotes = response.flatMap(({ data: { flexiblePackageQuotesV2 } }) => flexiblePackageQuotesV2);

      setLoading(false);
      setData(quotes);
    } catch (err) {
      setLoading(false);
      setError(err);
    }
  };

  return [data, update, { loading, error }];
}

export function useCreateFlexibleServiceQuotes({ slots, packageQuoteID, serviceID, locations, metadata }) {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ab] = useQueryParam('ab', BooleanParam);
  const [b] = useQueryParam('b', BooleanParam);

  const [mutate] = useMutation(CREATE_FLEXIBLE_SERVICE_QUOTES_V2);

  const update = async date => {
    setLoading(true);

    const context = getContext(ab, b);

    /*
      The react-trip-date-picker doesn't provide a safari friendly date for the new Date() func.
      We convert it ourselves so that safari doesn't get mad at us. 
    */
    const formattedDate = date.replaceAll('-', '/');
    const startDate = new Date(`${formattedDate} 00:00`);
    const endDate = new Date(`${formattedDate} 00:00`);

    const promises = slots.map(slot =>
      Promise.resolve(
        mutate({
          variables: {
            start: strScalarDate(subDays(startDate, 7)),
            end: strScalarDate(subDays(endDate, 1)),
            time: convertToMilitary(slot),
            packageQuoteID,
            locations: mapPropertyInfo(locations, metadata),
            serviceID,
          },
          ...context,
        }),
      ),
    );

    try {
      const response = await Promise.all(promises);
      const serviceQuotes = response.flatMap(({ data: { flexibleServiceQuotesV2 } }) => flexibleServiceQuotesV2);

      setLoading(false);
      setData(serviceQuotes);
    } catch (err) {
      setLoading(false);
      setError(err);
    }
  };

  return [data, update, { loading, error }];
}

export function usePackageQuoteQuery() {
  const [getPackageQuote, { data, loading, error }] = useLazyQuery(GET_PACKAGE_QUOTE_BY_ID);

  const getQuote = ({ packageQuoteID }) => {
    getPackageQuote({
      variables: {
        id: packageQuoteID,
      },
    });
  };

  return [
    data?.packageQuote,
    getQuote,
    {
      error,
      loading,
    },
  ];
}

export function useIsPackageAvailable() {
  const [getIsPackageAvailable, { data, loading, error }] = useLazyQuery(GET_PACKAGE_QUOTE_BY_ID_IS_AVAILABLE, {
    fetchPolicy: 'network-only',
  });

  const isPackageAvailable = id => {
    getIsPackageAvailable({
      variables: {
        id,
      },
    });
  };

  return [
    data?.packageQuote?.available,
    isPackageAvailable,
    {
      error,
      loading,
    },
  ];
}

export function useSaveQuote() {
  const [mutate, { data, loading, error }] = useMutation(SAVE_QUOTE);

  const update = async ({ quoteUrl, opportunityId, savedByUserEmail }) => {
    mutate({
      variables: {
        quoteURL: quoteUrl,
        LinkedOpportunitySFID: opportunityId,
        savedByUserEmail,
      },
    });
  };

  return [data?.saveQuoteURL, update, { loading, error }];
}
